// Authentication utilities for Cognito integration
import { COGNITO_CONFIG } from './common/constants';

/**
 * Cookie name for the Cognito ID token. Must match ID_TOKEN_COOKIE_NAME in
 * MrPibbCDK's edge auth Lambda (WebsiteHostingNestedStack/edgeAuthLambda) —
 * that Lambda@Edge reads this exact cookie to gate the CloudFront distribution.
 */
const ID_TOKEN_COOKIE_NAME = 'mrpibb_id_token';

export interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * Sets the ID token as a cookie readable by the edge auth Lambda (Lambda@Edge
 * can only inspect request cookies/headers, not localStorage). `Secure` is
 * omitted on http:// origins (local dev) since the browser silently drops
 * Secure cookies set over plain HTTP.
 */
function setIdTokenCookie(idToken: string): void {
  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${ID_TOKEN_COOKIE_NAME}=${encodeURIComponent(idToken)}; SameSite=Lax; Path=/${secureAttribute}`;
}

/** Clears the ID token cookie by setting an already-expired Max-Age. */
function clearIdTokenCookie(): void {
  document.cookie = `${ID_TOKEN_COOKIE_NAME}=; SameSite=Lax; Path=/; Max-Age=0`;
}

/**
 * Guards against concurrent `login()` calls triggering more than one
 * `window.location.href` navigation. Multiple independent call sites (boot's
 * `fetchConversations`, and `useConversationSessions`'s `openConnection` /
 * `reconnectSession`) each call `getValidToken()` and fall back to `login()`
 * on refresh failure — clicking a past conversation can trigger two of these
 * in the same tick. A second `location.href` assignment racing the first's
 * in-flight navigation is what produces the browser's "Unsafe attempt to
 * load URL ... from frame ..." error, since the frame is mid-navigation
 * from the first redirect. Module-level (not per-instance) because the
 * race is across call sites sharing one page, not across AuthService
 * instances — tests construct `new AuthService()` directly, which must not
 * bypass the guard.
 *
 * Reset on a `setTimeout(0)` rather than left permanently latched: an
 * assignment to `location.href` does not synchronously tear down the page —
 * the current page keeps running JS until the browser actually completes
 * the navigation. A permanent latch caused a real regression: once
 * `login()` ran once (e.g. the very first click after page load), every
 * subsequent call for the rest of that page's life became a silent no-op —
 * even after landing back on a fresh, authenticated page from a completed
 * redirect. Clearing it on the next tick still blocks same-tick concurrent
 * callers (the actual race this guard exists for) while letting a later,
 * genuinely new `login()` call (e.g. a second dead-refresh-token failure
 * five minutes later) redirect normally.
 */
let loginRedirectInFlight = false;

/** Test-only hook: resets the module-level login-redirect guard between tests. */
export function resetLoginRedirectGuardForTesting(): void {
  loginRedirectInFlight = false;
}

export class AuthService {
  private static instance: AuthService;
  private tokens: TokenResponse | null = null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Redirect to federated auth (Amazon Federate OIDC)
  login(): void {
    if (loginRedirectInFlight) {
      return;
    }
    loginRedirectInFlight = true;
    setTimeout(() => { loginRedirectInFlight = false; }, 0);

    const authUrl = `https://${COGNITO_CONFIG.domain}/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${COGNITO_CONFIG.clientId}&` +
      `redirect_uri=${encodeURIComponent(COGNITO_CONFIG.redirectUri)}&` +
      `scope=openid&` +
      `identity_provider=AmazonFederateCDK`;
    
    // Immediate redirect - no website content shown
    window.location.href = authUrl;
  }

  // Exchange authorization code for tokens
  async exchangeCodeForTokens(code: string): Promise<TokenResponse> {
    const tokenUrl = `https://${COGNITO_CONFIG.domain}/oauth2/token`;
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: COGNITO_CONFIG.clientId,
        code: code,
        redirect_uri: COGNITO_CONFIG.redirectUri,
      }),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const tokens: TokenResponse = await response.json();
    this.tokens = tokens;
    localStorage.setItem('mrpibb_tokens', JSON.stringify(tokens));
    setIdTokenCookie(tokens.id_token);
    return tokens;
  }

  // Get stored tokens
  getTokens(): TokenResponse | null {
    if (this.tokens) {
      return this.tokens;
    }

    // Try to load from localStorage
    const stored = localStorage.getItem('mrpibb_tokens');
    if (stored) {
      try {
        this.tokens = JSON.parse(stored);
        return this.tokens;
      } catch (error) {
        console.error('Error parsing stored tokens:', error);
        localStorage.removeItem('mrpibb_tokens');
      }
    }

    return null;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const tokens = this.getTokens();
    return tokens !== null;
  }

  // Check if the ID token is expired (with 60s buffer)
  isTokenExpired(): boolean {
    const token = this.getIdToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now() + 60000;
    } catch {
      return true;
    }
  }

  // Refresh tokens using the refresh token
  async refreshTokens(): Promise<boolean> {
    const tokens = this.getTokens();
    if (!tokens?.refresh_token) {
      return false;
    }

    try {
      const tokenUrl = `https://${COGNITO_CONFIG.domain}/oauth2/token`;
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: COGNITO_CONFIG.clientId,
          refresh_token: tokens.refresh_token,
        }),
      });

      if (!response.ok) {
        return false;
      }

      const newTokens: TokenResponse = await response.json();
      // Refresh response doesn't include refresh_token — keep the existing one
      this.tokens = { ...newTokens, refresh_token: tokens.refresh_token };
      localStorage.setItem('mrpibb_tokens', JSON.stringify(this.tokens));
      setIdTokenCookie(this.tokens.id_token);
      return true;
    } catch {
      return false;
    }
  }

  // Get access token for API calls
  getAccessToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.access_token || null;
  }

  // Get ID token for API Gateway Cognito authorizer
  getIdToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.id_token || null;
  }

  // Logout and redirect to federated logout
  logout(): void {
    this.tokens = null;
    localStorage.removeItem('mrpibb_tokens');
    clearIdTokenCookie();

    // Redirect to Cognito logout URL
    const logoutUrl = `https://${COGNITO_CONFIG.domain}/logout?` +
      `client_id=${COGNITO_CONFIG.clientId}&` +
      `logout_uri=${encodeURIComponent(COGNITO_CONFIG.redirectUri)}`;
    
    window.location.href = logoutUrl;
  }
}

export const authService = AuthService.getInstance();
